import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Link
} from '@mui/material';
import {
  LocalPharmacy,
  DeliveryDining,
  Person,
  Security,
  LocationOn,
  Phone
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <LocalPharmacy sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Pharmacies',
      description: 'Manage your inventory, receive orders, and connect with delivery riders seamlessly.',
      features: ['Product Management', 'Order Processing', 'Rider Network', 'Real-time Alerts']
    },
    {
      icon: <Person sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Customers',
      description: 'Browse medicines, place orders, and track deliveries from your favorite pharmacies.',
      features: [
        'Order your Prescription Medicines and other pharmaceuticals at https://pd-frontend.onrender.com',
        'Get it delivered to your door',
        'Convenient, Personalized, Fast and reliable',
        'Sign up and get Health and Wellness Tips'
        // 'Sign up and get Health Tips on our Health Tech Wellness innovation',
      ]
    },
    {
      icon: <DeliveryDining sx={{ fontSize: 40, color: 'success.main' }} />,
      title: 'Riders',
      description: 'Get delivery opportunities in your area and manage your delivery schedule.',
      features: ['Location-based Orders', 'Real-time Updates', 'Earnings Tracking', 'Flexible Schedule']
    }
  ];

  const securityFeatures = [
    {
      icon: <LocationOn sx={{ fontSize: 30 }} />,
      title: 'Real-time Location Tracking',
      description: 'Advanced GPS tracking with tampering detection'
    },
    {
      icon: <Security sx={{ fontSize: 30 }} />,
      title: 'Emergency Reporting',
      description: 'Report security incidents with photo/video evidence'
    },
    {
      icon: <Phone sx={{ fontSize: 30 }} />,
      title: 'Battery Monitoring',
      description: 'Track device status and detect potential tampering'
    }
  ];

  return (
    <Box>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box component="img" src="/pd-logo.png" alt="Prescription Drives" sx={{ height: 36, mr: 1 }} />
            <Typography variant="h6" component="div" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Prescription Drives
            </Typography>
          </Box>
          <Button color="primary" onClick={() => navigate('/login')}>
            Login
          </Button>
          <Button variant="contained" onClick={() => navigate('/register')} sx={{ ml: 1 }}>
            Sign Up
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1548A6 0%, #64B5F6 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h6" component="h1" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, letterSpacing: 0.5 }}>
            PRESCRIPTION DRIVES
          </Typography>
          <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 3, opacity: 0.95, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
            Health Tech Innovation for Wellness
          </Typography>
          <Typography variant={isMobile ? 'h4' : 'h3'} component="h3" gutterBottom fontWeight="bold" sx={{ mb: 2, opacity: 0.9 }}>
            Pharmacy Delivery Made Simple
          </Typography>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 4, opacity: 0.9 }}>
            Connect pharmacies, customers, and delivery riders in one seamless platform
          </Typography>
          

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
              onClick={() => navigate('/register')}
            >
              Get Started
            </Button>
            {/* <Button
              variant="outlined"
              size="large"
              sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              onClick={() => navigate('/emergency')}
            >
              Emergency Report
            </Button> */}
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom fontWeight="bold">
        A pharmacy, Customer or Delivery Rider? We've got you covered.
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
        PD App Health tech platform.
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {feature.description}
                  </Typography>
                  <Box>
                    {feature.features.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, pl: 0 }}>
                        <Typography variant="body2" sx={{ mr: 1, mt: 0, flexShrink: 0, minWidth: '16px' }}>
                          •
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1, mt: 0, lineHeight: 1.4, textAlign: 'left' }}>
                          {item.includes('https://') ? (
                            <>
                              {item.split('https://')[0]}
                              <Link href="https://pd-frontend.onrender.com" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                                https://pd-frontend.onrender.com
                              </Link>
                            </>
                          ) : (
                            item
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Security Features */}
       {/* <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom fontWeight="bold">
            Advanced Security Features
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Built with security and safety in mind
          </Typography>

          <Grid container spacing={4}>
            {securityFeatures.map((feature, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2, color: 'primary.main' }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Pricing Section */}
      {/* <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom fontWeight="bold">
          Simple Pricing
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Transparent pricing with location-based rates
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ textAlign: 'center', p: 4 }}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  Nigeria
                </Typography>
                <Typography variant="h3" color="primary.main" gutterBottom fontWeight="bold">
                  ₦250
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Per delivery within Nigeria
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ textAlign: 'center', p: 4 }}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  International
                </Typography>
                <Typography variant="h3" color="secondary.main" gutterBottom fontWeight="bold">
                  $1
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Per delivery outside Nigeria
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container> */}

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="h6" textAlign="center" gutterBottom>
            Prescription Drives - Pharmacy Delivery Platform
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.8, mb: 2 }}>
            Connecting pharmacies, customers, and delivery riders for a safer, more efficient healthcare delivery system.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
            <Phone sx={{ fontSize: 18 }} />
            <Typography variant="body1" component="a" href="tel:+2347045350299" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              +234 704 535 0299
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Promoted By Section */}
      <Box sx={{ bgcolor: 'white', py: 3, borderTop: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Promoted by Economic Growth and Development Center
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src="/economic-center-logo.png" alt="Economic Growth and Development Center" sx={{ height: 40 }} />
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                ECONOMIC CENTER
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
